import { FC, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';

// components
import { FormattedPrice } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedAuctionViewHistory } from '../PdpSharedAuctionViewHistory/PdpSharedAuctionViewHistory';
import { PdpSharedAuctionMessageContainer } from './PdpSharedAuctionMessageContainer';
import { PdpSharedBuyItNowButton } from '../shared/PdpSharedShoppingButtons/PdpSharedBuyItNowButton';

// types
import { PdpSharedAuctionActionCompletePurchase_item$key as Item } from './__generated__/PdpSharedAuctionActionCompletePurchase_item.graphql';
import { PdpSharedAuctionActionCompletePurchase_user$key } from './__generated__/PdpSharedAuctionActionCompletePurchase_user.graphql';
import { PdpSharedAuctionActionCompletePurchase_viewer$key } from './__generated__/PdpSharedAuctionActionCompletePurchase_viewer.graphql';
// styles
import styles from './PdpSharedAuctionActionCompletePurchase.scss';

type Props = {
    viewer: PdpSharedAuctionActionCompletePurchase_viewer$key;
    user: PdpSharedAuctionActionCompletePurchase_user$key | null;
    item: Item;
    currency: string;
};

type RelayVariables = {
    isClient: boolean;
    userId: string;
    itemId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const viewerFragment = graphql`
    fragment PdpSharedAuctionActionCompletePurchase_viewer on Viewer {
        isMultiItemCart: featureFlag(feature: "multiItemCart")
    }
`;

const userFragment = graphql`
    fragment PdpSharedAuctionActionCompletePurchase_user on User {
        ...PdpSharedBuyItNowButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpSharedAuctionActionCompletePurchase_item on Item {
        isSold
        lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
            checkoutDate
        }
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedPurchaseButton_item
        ...PdpSharedBuyItNowButton_item
    }
`;

const completePurchaseLabel = (
    <FormattedMessage
        id="PdpSharedAuctionActionCompletePurchase.completePurchase"
        defaultMessage="Complete Purchase"
    />
);

export const PdpSharedAuctionActionCompletePurchase: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const { isMultiItemCart } = viewer;
    const { currentBidAmount } = useAuctionPricing({ item, currency });
    const { userId, itemId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { isSold, lastTransaction } = item || {};
    const { checkoutDate } = lastTransaction || {};

    return (
        <div className={styles.container}>
            <span className={styles.closed} data-tn="pdp-bidding-closed">
                <FormattedMessage
                    id="PdpSharedAuctionActionCompletePurchase.biddingClosed"
                    defaultMessage="Bidding Closed"
                />
            </span>
            <div className={styles.congratulationsContainer}>
                <PdpSharedAuctionMessageContainer
                    text={
                        <span data-tn="pdp-auction-won-message">
                            <FormattedMessage
                                id="PdpSharedAuctionActionCompletePurchase.congratulations"
                                defaultMessage="<b>Congratulations!</b> You won this item."
                                values={{
                                    b: (chunks: ReactNode[]) => (
                                        <span className={styles.bold}>{chunks}</span>
                                    ),
                                }}
                            />
                        </span>
                    }
                />
            </div>
            <span className={styles.bold}>
                <FormattedMessage
                    id="PdpSharedAuctionActionCompletePurchase.winningBid"
                    defaultMessage="Your Winning Bid"
                />
            </span>
            {currentBidAmount && (
                <span className={styles.price} data-tn="pdp-winning-bid-amount">
                    <FormattedPrice value={currentBidAmount} currency={currency} />
                </span>
            )}
            {!(isSold || checkoutDate) && (
                <div className={styles.purchase}>
                    {isMultiItemCart ? (
                        <PdpSharedBuyItNowButton variant="auction" user={user} item={item} />
                    ) : (
                        <PdpSharedPurchaseButton
                            item={item}
                            itemId={itemId}
                            userId={userId}
                            quantitySelected={'1'}
                            label={completePurchaseLabel}
                        />
                    )}
                </div>
            )}
            <PdpSharedAuctionViewHistory itemId={itemId} currency={currency} />
        </div>
    );
};
